import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import AdminShell from "../../components/AdminShell";
import { toast } from "sonner";
import {
  Loader2, Phone, Mail, MapPin, Calendar, User,
  CheckCircle2, XCircle, PhoneCall, Clock, ChevronDown,
} from "lucide-react";

const STATUSES = [
  { value: "all",       label: "All",       colour: "#64748b" },
  { value: "pending",   label: "Pending",   colour: "#d97706" },
  { value: "contacted", label: "Contacted", colour: "#2563eb" },
  { value: "approved",  label: "Approved",  colour: "#16a34a" },
  { value: "rejected",  label: "Rejected",  colour: "#dc2626" },
];

const STATUS_META = {
  pending:   { label: "Pending",   bg: "bg-amber-50",  text: "text-amber-700",  border: "border-amber-200" },
  contacted: { label: "Contacted", bg: "bg-blue-50",   text: "text-blue-700",   border: "border-blue-200"  },
  approved:  { label: "Approved",  bg: "bg-green-50",  text: "text-green-700",  border: "border-green-200" },
  rejected:  { label: "Rejected",  bg: "bg-red-50",    text: "text-red-700",    border: "border-red-200"   },
};

function StatusBadge({ status }) {
  const m = STATUS_META[status] || STATUS_META.pending;
  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border ${m.bg} ${m.text} ${m.border}`}>
      {m.label}
    </span>
  );
}

function ActionMenu({ signup, onStatusChange, updating }) {
  const [open, setOpen] = useState(false);
  const actions = [
    { status: "contacted", label: "Mark as Contacted",  icon: PhoneCall,    colour: "#2563eb" },
    { status: "approved",  label: "Approve",            icon: CheckCircle2, colour: "#16a34a" },
    { status: "rejected",  label: "Reject",             icon: XCircle,      colour: "#dc2626" },
    { status: "pending",   label: "Reset to Pending",   icon: Clock,        colour: "#d97706" },
  ].filter(a => a.status !== signup.status);

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(o => !o)}
        disabled={updating}
        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-white border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50 transition-colors"
      >
        {updating ? <Loader2 size={12} className="animate-spin" /> : "Actions"}
        <ChevronDown size={12} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-1 z-20 bg-white border border-slate-200 rounded-xl shadow-lg py-1 min-w-[180px]">
            {actions.map(({ status, label, icon: Icon, colour }) => (
              <button
                key={status}
                onClick={() => { setOpen(false); onStatusChange(signup.id, status); }}
                className="flex items-center gap-2.5 w-full px-4 py-2.5 text-xs hover:bg-slate-50 transition-colors text-left"
                style={{ color: colour }}
              >
                <Icon size={13} />
                {label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function SignupRow({ signup, onStatusChange, updating }) {
  const name = `${signup.first_name} ${signup.last_name}`;
  const student = signup.role === "parent"
    ? `${signup.student_first_name || ""} ${signup.student_last_name || ""}`.trim()
    : name;
  const date = new Date(signup.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 hover:shadow-sm transition-shadow">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2.5 flex-wrap mb-3">
            <span className="font-semibold text-slate-900">{name}</span>
            <span className="text-xs text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full capitalize">{signup.role}</span>
            <StatusBadge status={signup.status} />
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-2 text-xs text-slate-600">
            <div className="flex items-center gap-1.5">
              <Mail size={12} className="text-slate-400 shrink-0" />
              <a href={`mailto:${signup.email}`} className="hover:text-blue-600 truncate">{signup.email}</a>
            </div>
            <div className="flex items-center gap-1.5">
              <Phone size={12} className="text-slate-400 shrink-0" />
              <a href={`tel:${signup.phone}`} className="hover:text-blue-600">{signup.phone}</a>
            </div>
            <div className="flex items-center gap-1.5">
              <MapPin size={12} className="text-slate-400 shrink-0" />
              {signup.branch}
            </div>
            <div className="flex items-center gap-1.5">
              <Calendar size={12} className="text-slate-400 shrink-0" />
              {date}
            </div>
          </div>

          {signup.role === "parent" && student && (
            <div className="mt-2.5 flex items-center gap-1.5 text-xs text-slate-500">
              <User size={12} className="text-slate-400" />
              <span>Child: <strong className="text-slate-700">{student}</strong> · Year {signup.year_level}</span>
            </div>
          )}
          {signup.role === "student" && (
            <div className="mt-2.5 text-xs text-slate-500">Year {signup.year_level}</div>
          )}
          {signup.notes && (
            <div className="mt-2.5 text-xs text-slate-500 bg-slate-50 rounded-lg px-3 py-2 italic">
              "{signup.notes}"
            </div>
          )}
        </div>

        <ActionMenu signup={signup} onStatusChange={onStatusChange} updating={updating === signup.id} />
      </div>
    </div>
  );
}

export default function AdminSignups() {
  const [signups, setSignups] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(null);
  const [stats, setStats] = useState({});

  const fetchSignups = async (status) => {
    setLoading(true);
    try {
      const [sigRes, statsRes] = await Promise.all([
        api.get(`/admin/signups?status=${status}`),
        api.get("/admin/stats"),
      ]);
      setSignups(sigRes.data.signups);
      setStats(statsRes.data);
    } catch {
      toast.error("Failed to load signups");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSignups(filter); }, [filter]);

  const handleStatusChange = async (id, status) => {
    setUpdating(id);
    try {
      await api.patch(`/admin/signups/${id}`, { status });
      toast.success(`Marked as ${status}`);
      fetchSignups(filter);
    } catch {
      toast.error("Failed to update status");
    } finally {
      setUpdating(null);
    }
  };

  const counts = STATUSES.reduce((acc, s) => {
    if (s.value === "all") acc[s.value] = stats.total_signups || 0;
    else if (s.value === "pending") acc[s.value] = stats.pending_signups || 0;
    return acc;
  }, {});

  return (
    <AdminShell title="Trial Signups" subtitle="Manage incoming trial lesson requests" stats={stats}>
      {/* Filter tabs */}
      <div className="flex gap-2 mb-6 flex-wrap">
        {STATUSES.map(s => (
          <button
            key={s.value}
            onClick={() => setFilter(s.value)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors border ${
              filter === s.value
                ? "text-white border-transparent"
                : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
            }`}
            style={filter === s.value ? { background: s.colour, borderColor: s.colour } : {}}
          >
            {s.label}
            {s.value === "pending" && (stats.pending_signups > 0) && (
              <span className="ml-1.5 bg-white/30 text-white text-[10px] px-1.5 py-0.5 rounded-full font-bold">
                {stats.pending_signups}
              </span>
            )}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <Loader2 size={28} className="animate-spin text-slate-400" />
        </div>
      ) : signups.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl py-16 text-center">
          <CheckCircle2 size={36} className="mx-auto text-green-400 mb-3" />
          <div className="font-medium text-slate-700">All caught up!</div>
          <div className="text-sm text-slate-400 mt-1">No {filter !== "all" ? filter : ""} signups right now.</div>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="text-xs text-slate-400 mb-2">{signups.length} result{signups.length !== 1 ? "s" : ""}</div>
          {signups.map(s => (
            <SignupRow key={s.id} signup={s} onStatusChange={handleStatusChange} updating={updating} />
          ))}
        </div>
      )}
    </AdminShell>
  );
}
